import clsx from 'clsx';
import { type KeyboardEvent, useRef } from 'react';
import { Link } from 'react-router-dom';

import styles from './SegmentedControl.module.scss';

export type SegmentedOption<T extends string> = {
  label: string;
  value: T;
  /** A tally beside the label — rendered as a chip, and left out entirely when 0. */
  count?: number;
};

type SegmentedControlBase<T extends string> = {
  options: readonly SegmentedOption<T>[];
  value: T;
  /**
   * `sm` is for a control sharing a row with something else — a card heading,
   * a toolbar. The default height is right when the control owns its row.
   */
  size?: 'md' | 'sm';
  /**
   * Keeps the control at its own width and size on phones too, instead of the narrow
   * full-width layout every other segmented control takes there. For a control that
   * shares its row with a label or heading, which a full-width control pushes off screen.
   */
  keepWidth?: boolean;
  /**
   * Stretches the control across its container on every screen, each segment taking an equal
   * share: for a control heading a form, so it lines up with the fields under it.
   */
  fullWidth?: boolean;
  /**
   * Which role the group announces. `tabs` for switching what a panel below shows,
   * `radio` for choosing one value in a form, `group` when neither fits.
   */
  as?: 'tabs' | 'radio' | 'group';
  className?: string;
  /** Names the control when the label is a separate element beside it. */
  'aria-labelledby'?: string;
  /** Names the control when nothing else on the row does. */
  'aria-label'?: string;
};

/**
 * Either the segments set local state, or they are navigation. Exactly one, so a caller
 * cannot half-wire both and leave the URL disagreeing with the UI.
 */
type SegmentedControlProps<T extends string> = SegmentedControlBase<T> &
  (
    | { onChange: (value: T) => void; hrefFor?: never }
    | {
        /**
         * Renders each segment as a real `<a href>` to the state it selects. Use it
         * whenever that state lives in the URL: the control then gets Back, reload,
         * share, middle-click and a status-bar preview for free, and the browser —
         * not a click handler — owns the transition.
         */
        hrefFor: (value: T) => string;
        onChange?: never;
      }
  );

const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  hrefFor,
  size = 'md',
  keepWidth = false,
  fullWidth = false,
  as = 'tabs',
  className,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) => {
  const groupRef = useRef<HTMLDivElement | null>(null);

  /* Arrow keys move between segments and the control keeps one tab stop. Not an extra:
     `tablist` and `radiogroup` promise it. Both roles tell assistive technology "Tab
     lands here once, then use the arrows", and a control that announces itself that way
     and then ignores the arrows has told the reader to do something that does nothing.
     Home and End because the roles carry those too. */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const index = options.findIndex((o) => o.value === value);
    if (index < 0) return;
    const last = options.length - 1;
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? last
          : // Wraps, the way a segmented control does: the ends of a small set of peers
            // are not a wall, and stopping dead there reads as broken.
            e.key === 'ArrowLeft' || e.key === 'ArrowUp'
            ? (index - 1 + options.length) % options.length
            : (index + 1) % options.length;
    if (next === index) return;
    e.preventDefault();
    const focusNext = () =>
      groupRef.current?.querySelectorAll<HTMLElement>('[role], [aria-pressed]')[next]?.focus();
    // Link variant: the anchor owns navigation, so move focus and let the reader press
    // it. Arrowing must not navigate — that turns a glance through the options into a
    // series of page loads.
    if (hrefFor) return focusNext();
    onChange(options[next].value);
    focusNext();
  };

  return (
    <div
      ref={groupRef}
      onKeyDown={onKeyDown}
      className={clsx(
        styles.segmented,
        size === 'sm' && styles.sm,
        keepWidth && styles.keepWidth,
        fullWidth && styles.fullWidth,
        className,
      )}
      role={as === 'tabs' ? 'tablist' : as === 'radio' ? 'radiogroup' : 'group'}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const selected = value === option.value;
        /* One selected-state attribute per role, and only one: a node carrying both
           aria-selected and aria-checked is a node with two answers. */
        const state =
          as === 'tabs'
            ? { role: 'tab' as const, 'aria-selected': selected }
            : as === 'radio'
              ? { role: 'radio' as const, 'aria-checked': selected }
              : { 'aria-pressed': selected };
        const shared = {
          ...state,
          /* Roving tabindex: the selected segment is the group's single tab stop, and
             the arrows do the rest. */
          tabIndex: selected ? 0 : -1,
          className: clsx(styles.option, selected && styles.optionActive),
          children: (
            <>
              {option.label}
              {option.count ? <span className={styles.count}>{option.count}</span> : null}
            </>
          ),
        };
        return hrefFor ? (
          <Link key={option.value} to={hrefFor(option.value)} {...shared} />
        ) : (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            {...shared}
          />
        );
      })}
    </div>
  );
};

export default SegmentedControl;
